import {
    createAction,
    Action,
    ActionFunction1,
} from 'redux-actions';
import {curry} from 'ramda';
import tryToCatch from 'try-to-catch';
import {setNotificationAction} from '@pluto/notification';
import {
    createQCJobTimespanNote,
    deleteQCJobTimespanNote,
    getQCJobTimespanNotes,
    TimespanNote,
    TimespanNoteBody,
    updateQCJobTimespanNote,
} from '@pluto/rest';
import {DispatchAny} from '@pluto/@types';

import {toggleSelectedMarkerAction} from 'VideoAudioQC/Timeline/Redux/TimelineActions';
import {QCReportMarker, TimelineRowType} from '../../TimelineTypes';
import {updateMarkerTimes} from './QCReportUtils';

export const QCREPORTS_SET = 'QCREPORTS_SET';
export const QCREPORTS_RESET = 'QCREPORTS_RESET';
export const QCREPORTS_ADD_MARKER = 'QCREPORTS_ADD_MARKER';
export const QCREPORTS_EDIT = 'QCREPORTS_EDIT';
export const QCREPORTS_DELETE = 'QCREPORTS_DELETE';

export type QCReportMarkersPromise = Promise<QCReportMarker[]>;
export type PartialQCReportMarker = Partial<QCReportMarker>;

export interface EditQCReportMarkerPayload {
    markerData: QCReportMarker;
}

export interface QCReportMarkerPayload {
    marker: QCReportMarker;
}

export const parseNote = (note: TimespanNote): QCReportMarker => {
    const {
        id,
        startFrameNumber,
        durationFrames,
        timespanCategoryId,
        value,
        createdByUser,
        updatedAt,
    } = note;
    
    let end;
    let start;
    
    if (startFrameNumber !== null && Number.isFinite(startFrameNumber) && durationFrames !== null && durationFrames > 0 && Number.isFinite(durationFrames)) {
        start = startFrameNumber;
        end = startFrameNumber + durationFrames;
    }
    
    return {
        identifier: String(id),
        details: value || '',
        end,
        issue: timespanCategoryId,
        start,
        createdByUser,
        updatedAt,
    };
};

export const parseNotes = (notes: TimespanNote[]): QCReportMarker[] => {
    if (!notes)
        return [];
    
    return notes.map(parseNote);
};

export const setQCReportsAction = createAction<QCReportMarkersPromise, string, number, number>(QCREPORTS_SET, async (url: string, start: number, end: number): QCReportMarkersPromise => {
    const [error, notes] = await tryToCatch(getQCJobTimespanNotes, url, {
        start,
        end,
        subtype: 'media',
    });
    
    if (!error) {
        return parseNotes(notes);
    }
    
    console.error('Failed to fetch Markup markers.', error);
    
    return [];
});

export const resetQCReportsAction = createAction<void>(QCREPORTS_RESET);

export const addQCReportMarkerAction = createAction<QCReportMarkerPayload, QCReportMarker>(QCREPORTS_ADD_MARKER, (marker: QCReportMarker): QCReportMarkerPayload => {
    return {
        marker,
    };
});

export const createQCReportMarkerAction = curry(async (url: string, duration: number, rowId: string, markerData: QCReportMarker, dispatch: DispatchAny) => {
    const {
        issue,
        details,
    } = markerData;
    const {
        start,
        end,
    } = updateMarkerTimes({
        marker: markerData,
        duration,
    });
    const body: TimespanNoteBody = {
        value: details,
        timespanCategoryId: issue,
        subtype: 'media',
    };
    
    if (start !== undefined && end !== undefined) {
        body.startFrameNumber = start;
        body.durationFrames = end - start;
    }
    
    const [error, note] = await tryToCatch(createQCJobTimespanNote, url, body);
    
    if (!error) {
        const marker = parseNote(note);
        
        dispatch(addQCReportMarkerAction(marker));
        dispatch(toggleSelectedMarkerAction(marker.identifier, rowId, TimelineRowType.QCReport));
        
        return;
    }
    
    dispatch(setNotificationAction({
        type: 'error',
        text: 'Failed to create QC Report Note.',
    }));
    
    console.error('Failed to create QC Report Note.', error);
});

export const editQCReportMarkerAction = createAction<EditQCReportMarkerPayload, QCReportMarker>(QCREPORTS_EDIT, (markerData: QCReportMarker): EditQCReportMarkerPayload => {
    return {
        markerData,
    };
});

export type UpdateQCReportMarkerActionProps = {
    url: string,
    duration: number,
    inpoint?: boolean,
    marker: QCReportMarker,
};

export const updateQCReportMarkerAction = curry(async (props: UpdateQCReportMarkerActionProps, dispatch: DispatchAny) => {
    const {
        url,
        duration,
        marker,
        inpoint,
    } = props;
    const {
        identifier,
        issue,
        details,
    } = marker;
    const updatedMarker = updateMarkerTimes({
        marker,
        duration,
        inpoint,
    });
    
    dispatch(editQCReportMarkerAction(updatedMarker));
    
    const {
        start,
        end,
    } = updatedMarker;
    const body: TimespanNoteBody = {
        subtype: 'media',
    };
    
    if (start !== undefined && end !== undefined) {
        body.startFrameNumber = start;
        body.durationFrames = end - start;
    } else {
        body.startFrameNumber = null;
        body.durationFrames = null;
    }
    
    if (issue)
        body.timespanCategoryId = issue;
    
    if (details)
        body.value = details;
    
    const markerUrl = `${url}/${identifier}`;
    const [error, savedMarker] = await tryToCatch(updateQCJobTimespanNote, markerUrl, body);
    
    if (error) {
        dispatch(setNotificationAction({
            type: 'error',
            text: 'Failed to save QC Report Note.',
        }));
        
        console.error('Failed to save QC Report Note.', error);
        
        return;
    }
    
    const {updatedAt} = savedMarker;
    dispatch(editQCReportMarkerAction({
        ...updatedMarker,
        updatedAt,
    }));
});

const deleteQCReportMarkerAction = createAction(QCREPORTS_DELETE);

export const removeQCReportMarkerAction = curry(async (url: string, markerId: string, dispatch: DispatchAny) => {
    dispatch(deleteQCReportMarkerAction(markerId));
    
    const markerUrl = `${url}/${markerId}`;
    const [error] = await tryToCatch(deleteQCJobTimespanNote, markerUrl);
    
    if (error) {
        dispatch(setNotificationAction({
            type: 'error',
            text: 'Failed to delete QC Report Note.',
        }));
        
        console.error('Failed to delete QC Report Note.', error);
    }
});

export type SetQCReportsAction = ActionFunction1<string, Action<QCReportMarkersPromise>>;
export type ResetQCReportsAction = ActionFunction1<void, Action<void>>;

export type CreateQCReportMarkerAction = (url: string, duration: number, rowId: string, markerData: QCReportMarker) => Promise<void>;
export type UpdateQCReportMarkerAction = (props: UpdateQCReportMarkerActionProps) => Promise<void>;
export type RemoveQCReportMarkerAction = (url: string, markerId: string) => Promise<void>;
